import { type FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography } from '@mui/material';

// common
import SchemaForm from 'common/SchemaForm';
import DynamicOperationCodeField from 'common/components/OperationCodeField/DynamicOperationCodeField';
import { formatDateWithoutTimezone } from 'common/utils';

// screen
import DialogWrapper from 'modules/suppliers/detail/components/modals/DialogWrapper';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import MuiLoading from 'common/components/MuiLoading';
import { FoodSafetyCertificate } from 'modules/suppliers/services/useFoodSafetyCertificate';
import useFoodCertificateDetails from 'modules/suppliers/services/useFoodCertificateDetails';
import PdfList from './PdfList';

interface CertificationDetailsProps {
  open: boolean;
  onClose: () => void;
  item: FoodSafetyCertificate;
}

interface CustomFormData extends FoodSafetyCertificate {
  customSwitch: boolean;
}

const CertificationDetails: FC<CertificationDetailsProps> = (props) => {
  const { open, item, onClose } = props;

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>('');

  const { formatMessage } = useIntl();

  const { params } = useTabsData();

  const { data } = useFoodCertificateDetails({
    supplierProfileOperationCode: params.supplierOperationCode,
    customerOperationCode: params.customerOperationCode,
    certificateOperationCode: item.operationCode,
  });

  if (!data) return <MuiLoading />;

  return (
    <DialogWrapper
      title={
        <>
          <FormattedMessage id="supplier.foodSafetyCertification.detailsModalTitle" />:{' '}
          {data.certificateNumber}
        </>
      }
      open={open}
      onClose={onClose}
    >
      <SchemaForm<CustomFormData>
        formHookProps={{
          values: {
            ...data,
            publishedAt: data.publishedAt ? formatDateWithoutTimezone(data.publishedAt) : '',
            expiredAt: data.isUnlimited
              ? formatMessage({ id: 'noExpiration' })
              : data.expiredAt
                ? formatDateWithoutTimezone(data.expiredAt)
                : '',
            customSwitch: data.expirationDate ? false : true,
            hasPdf: !!data.pdfUrl,
          },
        }}
        globalProps={{ readOnly: true }}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
            },
          }) => {
            return {
              certificateNumber: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.number',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.number',
                }),
                rules: { required: true },
              },
              operationCode: {
                mode: 'raw',
                propsWrapper: { desktop: 6 },
                render: (params) => (
                  <DynamicOperationCodeField
                    {...params}
                    label={formatMessage({
                      id: 'supplier.foodSafetyCertification.operationCode',
                    })}
                  />
                ),
              },
              name: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.name',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.name',
                }),
                rules: { required: true },
              },
              type: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.documentClassification',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.documentClassification',
                }),
                rules: { required: true },
              },
              publishedAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.issueDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.issueDate',
                }),
                rules: { required: true },
              },
              qualityStandard: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.productStandardAbbreviation',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.productStandardAbbreviation',
                }),
                rules: { required: true },
              },
              expiredAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.expirationDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.expirationDate',
                }),
                rules: { required: true },
              },
              issuedBy: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.issuePlace',
                }),
                placeholder: formatMessage({
                  id: 'supplier.foodSafetyCertification.issuePlace',
                }),
                rules: { required: true },
              },
              isUnlimited: {
                mode: 'switch',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'noExpiration' }),
                labelPlacement: 'top',
              },
              isPrimary: {
                mode: 'switch',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.foodSafetyCertification.mainCertificate',
                }),
                labelPlacement: 'top',
              },
              hasPdf: {
                mode: 'raw',
                render: () => {
                  const pdfPaths = (data.pdfPath as string[]) || [];

                  return !pdfPaths.length ? null : (
                    <Box>
                      <Typography sx={{ marginBottom: 1, fontWeight: 500 }}>
                        PDF Documents:
                      </Typography>
                      <Typography>
                        <PdfList
                          pdfPaths={pdfPaths}
                          onPdfClick={(pdfUrl, fileName) => {
                            setSelectedPdf(pdfUrl);
                            setSelectedPdfFileName(fileName);
                            setPdfModalOpen(true);
                          }}
                        />
                      </Typography>
                    </Box>
                  );
                },
              },
            };
          },
        }}
      />
      <PdfViewer
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdf}
        title={selectedPdfFileName || 'PDF Document'}
      />
    </DialogWrapper>
  );
};

export default CertificationDetails;
